import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { formatDate as sharedFormatDate } from '../utils/helpers';
import {
  Target, AlertCircle, Bell, TrendingUp, DollarSign,
  Users, Plus, Copy, Search, UserPlus, UserX, Check, X, Trash2, AlertTriangle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import CreditPurchaseDialog from '../components/shared/CreditPurchaseDialog';

const ProfilePage = () => {
  const {
    user,
    isAdmin,
    isPlayer,
    credits,
    logout,
    refreshCredits,
    refreshTrigger,
    notifications: allNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    fetchNotifications,
  } = useAuth();
  const { resetNotificationCount } = useWebSocketContext() || {};

  // Refresh the notifications list whenever the profile page mounts
  // or its parent triggers a refresh (e.g. after a bet posts a new
  // notification). Without this, the list snapshot taken at login
  // could go stale.
  useEffect(() => {
    if (user) fetchNotifications?.();
  }, [user, refreshTrigger, fetchNotifications]);
  const [profileData, setProfileData] = useState(null);
  const [bettingHistory, setBettingHistory] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(isAdmin ? 'settings' : 'overview');
  const [loading, setLoading] = useState(true);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [commissionHistory, setCommissionHistory] = useState([]);
  const [newHandle, setNewHandle] = useState('');
  const [handleCheckStatus, setHandleCheckStatus] = useState(null);
  const [leaderboardVisible, setLeaderboardVisible] = useState(false);
  const [leaderboardRank, setLeaderboardRank] = useState(null);
  const [savingHandle, setSavingHandle] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friendSearch, setFriendSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [privacySettings, setPrivacySettings] = useState({
    accept_friend_requests: true,
    bet_activity_visible_friends: false,
    civic_votes_visible_friends: false,
    leaderboard_visible: false
  });

  useEffect(() => {
    if (user) {
      fetchProfileData();
    }
  }, [user, refreshTrigger]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const results = {};
      
      try {
        const profileResponse = await api.profile.getMe();
        results.profileData = profileResponse.data;
      } catch (error) {
        console.error('Error fetching profile data:', error);
        toast.error('Failed to load profile information');
        results.profileData = null;
      }
      
      try {
        // Bets and civic votes are merged into a single timeline so
        // civic activity surfaces alongside predictions wherever bets
        // appear. Each entry carries `type: 'bet' | 'civic'` for the
        // renderer to dispatch on. We sort by the placed/cast time
        // descending so the newest item is on top regardless of source.
        const [betsResponse, civicResponse] = await Promise.all([
          api.profile.getBettingHistory().catch(() => ({ data: [] })),
          api.civic.getMyVotes().catch(() => ({ data: [] })),
        ]);
        const bets = (betsResponse.data || []).map((b) => ({ ...b, type: b.type || 'bet' }));
        const civic = (civicResponse.data || []).map((v) => ({ ...v, type: 'civic' }));
        const merged = [...bets, ...civic];
        merged.sort((a, b) => {
          const ta = new Date(a.placed_at || a.updated_at || a.created_at || 0).getTime();
          const tb = new Date(b.placed_at || b.updated_at || b.created_at || 0).getTime();
          return tb - ta;
        });
        results.bettingHistory = merged;
      } catch (error) {
        console.error('Error fetching betting/civic history:', error);
        results.bettingHistory = [];
      }
      
      try {
        const transactionsResponse = await api.profile.getTransactionHistory();
        results.transactionHistory = transactionsResponse.data;
      } catch (error) {
        console.error('Error fetching transaction history:', error);
        results.transactionHistory = [];
      }
      
      try {
        const referralResponse = await api.referrals.getUserStats();
        results.referralStats = referralResponse.data;
      } catch (error) {
        console.error('Error fetching referral stats:', error);
        results.referralStats = null;
      }
      
      try {
        const commissionsResponse = await api.profile.getCommissions();
        results.commissionHistory = commissionsResponse.data;
      } catch (error) {
        console.error('Error fetching commission history:', error);
        results.commissionHistory = [];
      }

      if (results.profileData) {
        setProfileData(results.profileData);
      }
      setBettingHistory(results.bettingHistory);
      setTransactionHistory(results.transactionHistory);
      setReferralStats(results.referralStats);
      setCommissionHistory(results.commissionHistory);
      
      if (results.profileData?.user) {
        setNewHandle(results.profileData.user.handle || '');
        setLeaderboardVisible(results.profileData.user.leaderboard_visible || false);
        setPrivacySettings({
          accept_friend_requests: results.profileData.user.accept_friend_requests ?? true,
          bet_activity_visible_friends: results.profileData.user.bet_activity_visible_friends ?? false,
          civic_votes_visible_friends: results.profileData.user.civic_votes_visible_friends ?? false,
          leaderboard_visible: results.profileData.user.leaderboard_visible ?? false
        });
      }

      // Fetch leaderboard rank
      try {
        const lbRes = await api.leaderboard.get();
        setLeaderboardRank(lbRes.data.my_rank);
      } catch {}

      // Fetch friends
      try {
        const [friendsRes, reqRes] = await Promise.all([
          api.friends.list(),
          api.friends.getRequests()
        ]);
        setFriends(friendsRes.data);
        setFriendRequests(reqRes.data);
      } catch {}
      
      if (!results.profileData) {
        toast.error('Critical: Unable to load profile information');
      }
    } catch (error) {
      console.error('Error in fetchProfileData:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = (newBalance) => {
    if (profileData) {
      setProfileData({
        ...profileData,
        stats: {
          ...profileData.stats,
          credits_balance: newBalance
        }
      });
    }
    refreshCredits();
    fetchProfileData();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
            <h2 className="text-2xl font-bold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-4 dark:text-gray-400">Please login to view your profile</p>
            <Link to="/login">
              <Button>Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Target className="h-12 w-12 text-emerald-600 mx-auto animate-spin dark:text-emerald-400" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  // formatDate liest die Browser-/User-Locale aus helpers.js -- damit
  // sind alle Datumsfelder konsistent (Civic-Votes, Bets,
  // Transactions, Commissions) und folgen automatisch der
  // Browser-Sprache.
  const formatDate = (dateString) => sharedFormatDate(dateString) || 'N/A';

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'text-green-600 bg-green-50';
      case 'lost': return 'text-red-600 bg-red-50';
      case 'placed': return 'text-blue-600 bg-blue-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Profile Header */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl sm:text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                    {profileData?.user?.handle ? profileData.user.handle.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div className="flex-1 text-center sm:text-left min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate dark:text-gray-100">
                    {profileData?.user?.handle || 'Loading...'}
                  </h1>
                  <p className="text-gray-600 text-sm sm:text-base truncate dark:text-gray-400">{profileData?.user?.email || 'Loading...'}</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-1">
                    <Badge variant={
                      profileData?.user?.roles?.includes('admin') ? 'destructive' :
                      profileData?.user?.roles?.includes('creator') ? 'default' : 
                      'secondary'
                    }>
                      {profileData?.user?.roles?.includes('admin') ? 'Admin' :
                       profileData?.user?.roles?.includes('creator') ? 'Creator' : 
                       'User'}
                    </Badge>
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      Since {formatDate(profileData?.user?.created_at)?.split(',')[0]}
                    </span>
                  </div>
                </div>
                {/* Credits + Buy Credits are player-only — admins
                    have no spendable balance and the player endpoints
                    are blocked for them server-side. */}
                {isPlayer && (
                  <div className="text-center sm:text-right flex-shrink-0">
                    <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                      {/* Live-Balance aus dem AuthContext (wird per refreshCredits
                          + WS wallet_changed aktuell gehalten, gleiche Quelle wie
                          der Header). Der Profil-Snapshot ist nur Fallback beim
                          ersten Render, bevor credits geladen ist. */}
                      {(credits ?? profileData?.stats?.credits_balance ?? 0).toLocaleString()}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Credits Balance</p>
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => setShowPurchaseDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Buy Credits
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Player Stats — only shown to players. Admins have no
              meaningful values for any of these (all zero). */}
          {isPlayer && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-6 mb-8">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {profileData?.stats?.total_bets ?? 0}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Bets</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {profileData?.stats?.winning_bets ?? 0}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Winning Bets</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {profileData?.stats?.win_rate ?? 0}%
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Win Rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {profileData?.stats?.total_wagered ? profileData.stats.total_wagered.toLocaleString() : '0'}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Wagered</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {profileData?.stats?.total_civic_votes ?? 0}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Civic Stimmen</p>
                {(profileData?.stats?.total_civic_votes ?? 0) > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1 dark:text-gray-500">
                    {profileData?.stats?.civic_votes_yes ?? 0} JA · {profileData?.stats?.civic_votes_no ?? 0} NEIN
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* Tabs — admins see only Settings (notifications live im
              Admin-Panel, Player-Tabs sind für Player). */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Desktop Tabs */}
            <TabsList className={`hidden md:grid w-full ${isAdmin ? 'grid-cols-1' : 'grid-cols-6'}`}>
              {isPlayer && <TabsTrigger value="overview">Overview</TabsTrigger>}
              {isPlayer && <TabsTrigger value="bets">Betting History</TabsTrigger>}
              {isPlayer && <TabsTrigger value="transactions">Transactions</TabsTrigger>}
              {isPlayer && <TabsTrigger value="referrals">Referrals</TabsTrigger>}
              {isPlayer && <TabsTrigger value="friends" data-testid="profile-friends-tab">Freunde</TabsTrigger>}
              <TabsTrigger value="settings" data-testid="profile-settings-tab">Settings</TabsTrigger>
            </TabsList>

            {/* Mobile Tabs - Dropdown */}
            <div className="md:hidden w-full mb-4">
              <select 
                value={activeTab} 
                onChange={(e) => setActiveTab(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none dark:bg-slate-900/80 dark:border-slate-700"
                style={{
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  backgroundSize: '1rem'
                }}
              >
                {isPlayer && <option value="overview">Overview</option>}
                {isPlayer && <option value="bets">Betting History</option>}
                {isPlayer && <option value="transactions">Transactions</option>}
                {isPlayer && <option value="referrals">Referrals & Commissions</option>}
                {isPlayer && <option value="friends">Freunde</option>}
                <option value="settings">Settings</option>
              </select>
            </div>
            
            {isPlayer && (
            <TabsContent value="overview" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        Notifications
                      </CardTitle>
                      <CardDescription>Your recent notifications and updates</CardDescription>
                    </div>
                    {allNotifications?.some(n => !n.read) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0 self-start"
                        onClick={async () => {
                          await markAllNotificationsAsRead();
                          resetNotificationCount?.(0);
                        }}
                      >
                        Alle als gelesen
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {allNotifications?.length === 0 ? (
                        <div className="text-center py-8">
                          <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
                          <p className="text-gray-500 dark:text-gray-400">No notifications yet</p>
                          <p className="text-sm text-gray-400 dark:text-gray-500">Your notifications will appear here</p>
                        </div>
                      ) : (
                        allNotifications?.map((notification) => (
                          <div
                            key={notification.id}
                            className={`flex items-start space-x-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                              notification.read
                                ? 'opacity-60 bg-gray-25 dark:bg-slate-900/40 border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/60'
                                : 'bg-white dark:bg-slate-900/80 dark:backdrop-blur-sm border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/80'
                            }`}
                            onClick={async () => {
                              if (notification.read) return;
                              await markNotificationAsRead(notification.id);
                              resetNotificationCount?.(Math.max(0, (allNotifications?.filter(n => !n.read).length || 1) - 1));
                            }}
                          >
                            <div className={`mt-2 w-3 h-3 rounded-full flex-shrink-0 ${
                              notification.read ? 'bg-gray-300 dark:bg-slate-600' : (
                                notification.priority === 'high' ? 'bg-rose-400' :
                                notification.priority === 'medium' ? 'bg-amber-400' : 'bg-blue-400'
                              )
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                                <h4 className={`font-medium min-w-0 break-words ${notification.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                  {notification.title}
                                </h4>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Badge variant="outline" className="text-xs dark:border-slate-700 dark:text-gray-300">
                                    {notification.type.replace('_', ' ').toUpperCase()}
                                  </Badge>
                                  {!notification.read && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <p className={`text-sm mt-1 ${notification.read ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                                {notification.message}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {new Date(notification.created_at).toLocaleString()}
                                </p>
                                {notification.read && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">Read</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {bettingHistory.slice(0, 5).map((entry) => {
                        const isCivic = entry.type === 'civic';
                        const title = isCivic ? entry.market_title : entry.legs?.[0]?.market_title;
                        const subtitle = isCivic
                          ? (entry.direction === 'yes' ? 'JA' : 'NEIN')
                          : entry.legs?.[0]?.outcome_label;
                        return (
                          <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                            <div>
                              <p className="font-medium text-sm">{title}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">{subtitle}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">
                                {isCivic ? 'Stimme abgegeben' : `${entry.stake} credits`}
                              </p>
                              <Badge className={getStatusColor(entry.status)} variant="secondary">
                                {isCivic ? 'civic' : entry.status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                      {bettingHistory.length === 0 && (
                        <p className="text-gray-500 text-center py-4 dark:text-gray-400">No betting activity yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            )}

            <TabsContent value="bets" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Betting History</CardTitle>
                  <CardDescription>Complete history of your bets and civic votes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {bettingHistory.map((entry) => {
                      const isCivic = entry.type === 'civic';
                      if (isCivic) {
                        return (
                          <div key={entry.id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300">civic</Badge>
                                  <Badge className={getStatusColor(entry.status)} variant="secondary">{entry.status}</Badge>
                                  {entry.civic_category && (
                                    <Badge variant="outline" className="text-[10px]">{entry.civic_category}</Badge>
                                  )}
                                </div>
                                <p className="font-medium">{entry.market_title || 'Civic-Markt'}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Stimme: <span className="font-medium">
                                    {entry.direction === 'yes' ? 'JA' : 'NEIN'}
                                  </span>
                                  {' · '}
                                  Aktuell {entry.yes_percent}% JA · {entry.total_votes} Stimmen
                                </p>
                              </div>
                              <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(entry.updated_at)}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                      <div key={entry.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge variant="outline">{entry.type}</Badge>
                              <Badge className={getStatusColor(entry.status)} variant="secondary">
                                {entry.status}
                              </Badge>
                            </div>
                            {entry.legs?.map((leg, index) => (
                              <div key={index} className="mb-2">
                                <p className="font-medium">{leg.market_title}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Betting on: <span className="font-medium">{leg.outcome_label}</span>
                                  at {leg.locked_price ? `${leg.locked_price.toFixed(2)} VC` : '—'}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{entry.stake} VC</p>
                            {/* Bei settled Bets den tatsächlichen Payout
                                anzeigen (oder 0 bei Loss). Für aktive
                                Bets keinen Schätzwert mehr — die parimutuel-
                                Pools verschieben sich, Anzeige war oft
                                irreführend (Est. Payout < Stake). */}
                            {entry.status === 'won' && entry.payout != null && (
                              <p className="text-sm text-emerald-600 font-medium dark:text-emerald-400">
                                Payout: {Number(entry.payout).toFixed(2)} VC
                              </p>
                            )}
                            {entry.status === 'lost' && (
                              <p className="text-sm text-red-500 dark:text-rose-400">Verloren</p>
                            )}
                            {entry.status === 'refunded' && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">Refund</p>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(entry.placed_at)}</p>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                    {bettingHistory.length === 0 && (
                      <div className="text-center py-8">
                        <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
                        <p className="text-gray-500 dark:text-gray-400">No bets placed yet</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Start betting on markets to see your history here</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="transactions" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Transaction History</CardTitle>
                  <CardDescription>All credits transactions and account activities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      ...transactionHistory.map(tx => ({...tx, source: 'transaction'})),
                      ...commissionHistory.filter(c => c.status === 'paid').map(c => ({
                        id: c.id,
                        type: 'credit',
                        amount: c.amount,
                        reason: `${c.type}_commission`,
                        created_at: c.created_at,
                        status: 'success',
                        source: 'commission',
                        commission_type: c.type,
                        commission_percentage: c.percentage
                      }))
                    ]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((transaction) => (
                      <div key={`${transaction.source}-${transaction.id}`} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className={`p-2 rounded-full ${
                            transaction.source === 'commission' ? 'bg-yellow-100' :
                            transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {transaction.source === 'commission' ? (
                              <DollarSign className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                            ) : transaction.type === 'credit' ? (
                              <TrendingUp className="h-4 w-4 text-green-600" />
                            ) : (
                              <DollarSign className="h-4 w-4 text-red-600 dark:text-rose-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium capitalize">
                              {transaction.source === 'commission' 
                                ? `${transaction.commission_type.replace('_', ' ')} Commission` 
                                : transaction.reason}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(transaction.created_at)}</p>
                            {transaction.source === 'commission' && transaction.commission_percentage != null && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {(transaction.commission_percentage * 100).toFixed(1)}% commission
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            transaction.source === 'commission' ? 'text-yellow-600' :
                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.type === 'credit' ? '+' : '-'}{transaction.amount} credits
                          </p>
                          <Badge variant={
                            transaction.source === 'commission' ? 'outline' :
                            transaction.status === 'success' ? 'default' : 'secondary'
                          }>
                            {transaction.source === 'commission' ? 'Commission' : transaction.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {(transactionHistory.length === 0 && commissionHistory.length === 0) && (
                      <div className="text-center py-8">
                        <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
                        <p className="text-gray-500 dark:text-gray-400">No transactions yet</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Your credit transactions and commission earnings will appear here</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Referrals & Commissions Tab */}
            <TabsContent value="referrals" className="mt-6">
              <div className="space-y-6">
                {/* Referral Program Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Referral Program
                    </CardTitle>
                    <CardDescription>
                      Invite friends and earn commissions on their credit purchases
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {referralStats && (
                      <>
                        {/* Referral Link Section */}
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30">
                          <h4 className="font-medium text-emerald-800 mb-3 dark:text-emerald-200">Your Referral Link</h4>
                          <div className="flex gap-2 items-center">
                            <Input
                              value={referralStats.referral_link}
                              readOnly
                              className="bg-white dark:bg-slate-900/80"
                            />
                            <Button
                              onClick={() => {
                                navigator.clipboard.writeText(referralStats.referral_link);
                                toast.success('Referral link copied to clipboard!');
                              }}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy
                            </Button>
                          </div>
                          <p className="text-sm text-emerald-600 mt-2 dark:text-emerald-400">
                            Share this link to earn commissions when friends sign up and purchase credits
                          </p>
                        </div>

                        {/* Stats Overview */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {referralStats.stats.total_referrals_l1 + referralStats.stats.total_referrals_l2}
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm font-medium">Direct Referrals</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-green-600">
                                {referralStats.stats.total_referrals_l1}
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm font-medium">Indirect Referrals</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                {referralStats.stats.total_referrals_l2}
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                                {(referralStats.stats.total_commission_l1 + referralStats.stats.total_commission_l2 + referralStats.stats.total_creator_commission).toLocaleString()}
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Referral Lists */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Direct Referrals</CardTitle>
                              <CardDescription>People who signed up with your link</CardDescription>
                            </CardHeader>
                            <CardContent>
                              {referralStats.referrals.level_1.length === 0 ? (
                                <p className="text-gray-500 text-center py-4 dark:text-gray-400">No direct referrals yet</p>
                              ) : (
                                <div className="space-y-3">
                                  {referralStats.referrals.level_1.slice(0, 5).map((referral) => (
                                    <div key={referral.id} className="flex justify-between items-center p-3 bg-gray-50 rounded dark:bg-slate-800/60">
                                      <div>
                                        <div className="font-medium">{referral.handle}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                          Joined: {new Date(referral.created_at).toLocaleDateString()}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Indirect Referrals</CardTitle>
                              <CardDescription>Referrals from your direct referrals</CardDescription>
                            </CardHeader>
                            <CardContent>
                              {referralStats.referrals.level_2.length === 0 ? (
                                <p className="text-gray-500 text-center py-4 dark:text-gray-400">No indirect referrals yet</p>
                              ) : (
                                <div className="space-y-3">
                                  {referralStats.referrals.level_2.slice(0, 5).map((referral) => (
                                    <div key={referral.id} className="flex justify-between items-center p-3 bg-gray-50 rounded dark:bg-slate-800/60">
                                      <div>
                                        <div className="font-medium">{referral.handle}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                          Joined: {new Date(referral.created_at).toLocaleDateString()}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Commission Overview Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Commission Overview
                    </CardTitle>
                    <CardDescription>
                      Your earnings from creator and referral commissions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {commissionHistory.length === 0 ? (
                      <div className="text-center py-8">
                        <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No commission earnings yet</p>
                        <p className="text-sm text-gray-400 mt-2 dark:text-gray-500">
                          Start creating markets or referring friends to earn commissions
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Commission Summary Stats */}
                        {referralStats && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <Card className="border-emerald-200 dark:border-emerald-500/30">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Creator Earnings</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                                  {referralStats.stats.total_creator_commission.toLocaleString()} Credits
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="border-blue-200 dark:border-blue-500/30">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Referral L1</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                  {referralStats.stats.total_commission_l1.toLocaleString()} Credits
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="border-purple-200">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-purple-700">Referral L2</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                  {referralStats.stats.total_commission_l2.toLocaleString()} Credits
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Commission History Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                                <th className="p-3 text-left font-medium">Date</th>
                                <th className="p-3 text-left font-medium">Type</th>
                                <th className="p-3 text-left font-medium">Amount</th>
                                <th className="p-3 text-left font-medium">Rate</th>
                                <th className="p-3 text-left font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {commissionHistory.slice(0, 20).map((commission) => (
                                <tr key={commission.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                                  <td className="p-3">
                                    {formatDate(commission.created_at)}
                                  </td>
                                  <td className="p-3">
                                    <Badge variant={
                                      commission.type === 'creator' ? 'default' :
                                      commission.type === 'referral_l1' ? 'secondary' :
                                      'outline'
                                    }>
                                      {commission.type === 'creator' ? 'Creator' :
                                       commission.type === 'referral_l1' ? 'Referral L1' :
                                       'Referral L2'}
                                    </Badge>
                                  </td>
                                  <td className="p-3 font-medium text-green-600">
                                    +{commission.amount.toLocaleString()} Credits
                                  </td>
                                  <td className="p-3">
                                    {commission.percentage != null
                                      ? `${(commission.percentage * 100).toFixed(1)}%`
                                      : '—'}
                                  </td>
                                  <td className="p-3">
                                    <Badge variant={commission.status === 'paid' ? 'default' : 'secondary'}>
                                      {commission.status}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>


            {/* Friends Tab */}
            <TabsContent value="friends" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Friend Search */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Search size={18} /> Freunde suchen</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Username suchen..."
                        value={friendSearch}
                        onChange={(e) => setFriendSearch(e.target.value)}
                        data-testid="friend-search-input"
                      />
                      <Button onClick={async () => {
                        if (friendSearch.length < 2) return;
                        try {
                          const res = await api.profile.searchUsers(friendSearch);
                          setSearchResults(res.data);
                        } catch {}
                      }} data-testid="friend-search-btn">
                        <Search size={16} />
                      </Button>
                    </div>
                    {searchResults.map(u => (
                      <div key={u.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                        <span className="text-sm font-medium">{u.handle}</span>
                        <Button size="sm" variant="outline" onClick={async () => {
                          try {
                            await api.friends.requestByHandle(u.handle);
                            toast.success(`Anfrage an ${u.handle} gesendet`);
                            setSearchResults([]);
                            setFriendSearch('');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Fehler');
                          }
                        }} data-testid={`friend-add-${u.handle}`}>
                          <UserPlus size={14} className="mr-1" /> Hinzufügen
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Friend Requests */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Bell size={18} /> Anfragen ({friendRequests.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {friendRequests.length === 0 && <p className="text-sm text-gray-400 dark:text-gray-500">Keine offenen Anfragen</p>}
                    {friendRequests.map(r => (
                      <div key={r.friendship_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                        <span className="text-sm font-medium">{r.handle}</span>
                        <div className="flex gap-1.5">
                          <Button size="sm" className="h-7" onClick={async () => {
                            try {
                              await api.friends.accept(r.friendship_id);
                              toast.success(`${r.handle} als Freund hinzugefügt`);
                              fetchProfileData();
                            } catch { toast.error('Fehler'); }
                          }} data-testid={`friend-accept-${r.friendship_id}`}>
                            <Check size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7" onClick={async () => {
                            try {
                              await api.friends.reject(r.friendship_id);
                              toast.success('Anfrage abgelehnt');
                              fetchProfileData();
                            } catch { toast.error('Fehler'); }
                          }}>
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Current Friends */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Users size={18} /> Meine Freunde ({friends.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {friends.length === 0 && <p className="text-sm text-gray-400 dark:text-gray-500">Noch keine Freunde hinzugefügt</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {friends.map(f => (
                      <div key={f.friendship_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                        <span className="text-sm font-medium">{f.handle}</span>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 h-7 dark:hover:text-rose-400" onClick={async () => {
                          try {
                            await api.friends.remove(f.friendship_id);
                            toast.success('Freundschaft beendet');
                            fetchProfileData();
                          } catch { toast.error('Fehler'); }
                        }} data-testid={`friend-remove-${f.friendship_id}`}>
                          <UserX size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            {/* Settings Tab */}
            <TabsContent value="settings" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Username Change */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Username ändern</CardTitle>
                    <CardDescription>Dein Anzeigename in der App</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Neuer Username</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            value={newHandle}
                            onChange={(e) => {
                              setNewHandle(e.target.value);
                              setHandleCheckStatus(null);
                              if (e.target.value.length >= 2 && e.target.value !== profileData?.user?.handle) {
                                setHandleCheckStatus('checking');
                                const val = e.target.value;
                                setTimeout(async () => {
                                  try {
                                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                                    const res = await fetch(`${API_URL}/api/auth/check-handle?handle=${encodeURIComponent(val)}`);
                                    const data = await res.json();
                                    setHandleCheckStatus(data.available ? 'available' : 'taken');
                                  } catch {
                                    setHandleCheckStatus(null);
                                  }
                                }, 400);
                              }
                            }}
                            placeholder="Neuer Username"
                            data-testid="settings-handle-input"
                          />
                          {handleCheckStatus === 'available' && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-sm">&#10003;</span>
                          )}
                          {handleCheckStatus === 'taken' && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-sm dark:text-rose-400">&#10007;</span>
                          )}
                        </div>
                        <Button 
                          disabled={savingHandle || handleCheckStatus === 'taken' || !newHandle || newHandle === profileData?.user?.handle || newHandle.length < 2}
                          data-testid="settings-save-handle-btn"
                          onClick={async () => {
                            setSavingHandle(true);
                            try {
                              await api.profile.updateHandle(newHandle);
                              toast.success('Username geändert!');
                              fetchProfileData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Fehler beim Ändern');
                            } finally {
                              setSavingHandle(false);
                            }
                          }}
                        >
                          {savingHandle ? '...' : 'Speichern'}
                        </Button>
                      </div>
                      {handleCheckStatus === 'taken' && (
                        <p className="text-xs text-red-500 dark:text-rose-400">Username ist bereits vergeben</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Passwort ändern */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Passwort ändern</CardTitle>
                    <CardDescription>Aktuelles Passwort bestätigen, neues setzen</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <Label>Aktuelles Passwort</Label>
                      <PasswordInput
                        value={currentPw}
                        onChange={(e) => setCurrentPw(e.target.value)}
                        placeholder="Aktuelles Passwort"
                        autoComplete="current-password"
                        data-testid="settings-current-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Neues Passwort</Label>
                      <PasswordInput
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        placeholder="Mindestens 8 Zeichen"
                        autoComplete="new-password"
                        data-testid="settings-new-password"
                      />
                      <p
                        className={`text-xs flex items-center gap-1.5 ${
                          newPw.length >= 8
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        <span>{newPw.length >= 8 ? '✓' : '•'}</span>
                        Mindestens 8 Zeichen
                      </p>
                    </div>
                    <Button
                      disabled={savingPw || !currentPw || newPw.length < 8}
                      data-testid="settings-save-password-btn"
                      onClick={async () => {
                        setSavingPw(true);
                        try {
                          await api.auth.changePassword({ current_password: currentPw, new_password: newPw });
                          toast.success('Passwort geändert!');
                          setCurrentPw('');
                          setNewPw('');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Fehler beim Ändern');
                        } finally {
                          setSavingPw(false);
                        }
                      }}
                    >
                      {savingPw ? '...' : 'Passwort ändern'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Leaderboard & Sichtbarkeit — player-only (admins
                    erscheinen nicht im Leaderboard). */}
                {isPlayer && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Leaderboard & Sichtbarkeit</CardTitle>
                    <CardDescription>Steuere ob dein Profil im öffentlichen Ranking erscheint</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                      <div>
                        <p className="font-medium text-sm">Auf Leaderboard erscheinen</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {leaderboardVisible ? 'Dein Name ist öffentlich sichtbar' : 'Du bist aktuell nicht im Ranking sichtbar'}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={savingVisibility}
                        data-testid="settings-leaderboard-toggle"
                        onClick={async () => {
                          const newVal = !leaderboardVisible;
                          setSavingVisibility(true);
                          try {
                            await api.profile.updateLeaderboardVisibility(newVal);
                            setLeaderboardVisible(newVal);
                            toast.success(newVal ? 'Du erscheinst jetzt im Leaderboard!' : 'Leaderboard-Sichtbarkeit deaktiviert');
                          } catch {
                            toast.error('Fehler beim Aktualisieren');
                          } finally {
                            setSavingVisibility(false);
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          leaderboardVisible ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          leaderboardVisible ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {leaderboardRank && (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 dark:bg-emerald-500/10">
                        <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                          Dein aktueller Rang:
                        </p>
                        <div className="mt-1 space-y-1">
                          {leaderboardRank.winner_rank ? (
                            <p className="text-sm text-emerald-700 dark:text-emerald-400">
                              Top Gewinner: <span className="font-bold">#{leaderboardRank.winner_rank}</span> von {leaderboardRank.winner_total} Spielern
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-400">Noch nicht im Gewinner-Ranking</p>
                          )}
                          {leaderboardRank.roi_rank ? (
                            <p className="text-sm text-emerald-700 dark:text-emerald-400">
                              Beste ROI: <span className="font-bold">#{leaderboardRank.roi_rank}</span> von {leaderboardRank.roi_total} Spielern
                              ({leaderboardRank.roi_percent > 0 ? '+' : ''}{leaderboardRank.roi_percent}%)
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {leaderboardRank.total_bets < 5
                                ? `Noch ${5 - leaderboardRank.total_bets} Wetten bis zum ROI-Ranking`
                                : 'Noch nicht im ROI-Ranking'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                )}

                {/* Datenschutz & Sichtbarkeit — alle Toggles sind
                    player-spezifisch (Freunde, Bets, Civic, Leaderboard). */}
                {isPlayer && (
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Datenschutz & Sichtbarkeit</CardTitle>
                    <CardDescription>Steuere wer was von dir sehen kann</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { key: 'accept_friend_requests', label: 'Freundschaftsanfragen annehmen', desc: 'Andere können dir Freundschaftsanfragen senden' },
                      { key: 'bet_activity_visible_friends', label: 'Wett-Aktivität für Freunde sichtbar', desc: 'Freunde sehen dass du auf einen Markt gewettet hast' },
                      { key: 'civic_votes_visible_friends', label: 'Civic-Abstimmungen für Freunde sichtbar', desc: 'Civic-Abstimmungen gelten als politische Meinungsäußerungen und sind nach DSGVO Art. 9 besonders geschützt', warn: true },
                      { key: 'leaderboard_visible', label: 'Auf globalem Leaderboard erscheinen', desc: 'Dein Username wird öffentlich im Ranking angezeigt' },
                    ].map(toggle => (
                      <div key={toggle.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                        <div className="flex-1 mr-4">
                          <p className="text-sm font-medium">{toggle.label}</p>
                          <p className={`text-xs ${toggle.warn ? 'text-amber-600' : 'text-gray-400'}`}>{toggle.desc}</p>
                        </div>
                        <button
                          type="button"
                          data-testid={`privacy-toggle-${toggle.key}`}
                          onClick={async () => {
                            const newVal = !privacySettings[toggle.key];
                            try {
                              await api.profile.updatePrivacy(toggle.key, newVal);
                              setPrivacySettings(prev => ({ ...prev, [toggle.key]: newVal }));
                              if (toggle.key === 'leaderboard_visible') setLeaderboardVisible(newVal);
                              toast.success('Einstellung gespeichert');
                            } catch { toast.error('Fehler'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                            privacySettings[toggle.key] ? 'bg-emerald-500' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            privacySettings[toggle.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                )}

                {/* Gefahrenzone — Account löschen */}
                <Card className="lg:col-span-2 border-rose-200 dark:border-rose-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg text-rose-700 dark:text-rose-400 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Gefahrenzone
                    </CardTitle>
                    <CardDescription>Unwiderrufliche Aktionen für deinen Account</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-rose-800 dark:text-rose-300">Konto löschen</p>
                        <p className="text-xs text-rose-700/80 dark:text-rose-400/80">
                          Entfernt dein Konto und alle zugehörigen Daten (Wetten, Credits, Verlauf) endgültig. Das lässt sich nicht rückgängig machen.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="flex-shrink-0 self-start border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 dark:border-rose-500/40 dark:text-rose-400 dark:hover:bg-rose-500/15"
                        onClick={() => { setDeleteConfirmText(''); setShowDeleteDialog(true); }}
                        data-testid="delete-account-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Konto löschen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Credit Purchase Dialog */}
      {showPurchaseDialog && (
        <CreditPurchaseDialog
          isOpen={showPurchaseDialog}
          onClose={() => setShowPurchaseDialog(false)}
          onPurchaseSuccess={handlePurchaseSuccess}
        />
      )}

      {/* Konto-Löschen-Bestätigung — Tippen-zum-Bestätigen, da irreversibel */}
      <Dialog open={showDeleteDialog} onOpenChange={(o) => { if (!deletingAccount) setShowDeleteDialog(o); }}>
        <DialogContent className="dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-left flex items-center gap-2 text-rose-700 dark:text-rose-400">
              <AlertTriangle className="h-5 w-5" />
              Konto endgültig löschen?
            </DialogTitle>
            <DialogDescription className="text-left dark:text-gray-400">
              Dein Konto und alle zugehörigen Daten (Wetten, Credits, Verlauf, Benachrichtigungen) werden unwiderruflich gelöscht. Von dir erstellte Märkte bleiben bestehen, sind dann aber keinem Konto mehr zugeordnet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm dark:text-gray-300">
              Zum Bestätigen <span className="font-bold">LÖSCHEN</span> eingeben:
            </Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="LÖSCHEN"
              data-testid="delete-account-confirm-input"
              className="dark:bg-slate-800 dark:border-slate-700"
            />
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                className="flex-1 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-800"
                disabled={deletingAccount}
                onClick={() => setShowDeleteDialog(false)}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white dark:!bg-none dark:!bg-rose-600 dark:hover:!bg-rose-500 dark:!text-white"
                disabled={deleteConfirmText.trim() !== 'LÖSCHEN' || deletingAccount}
                data-testid="delete-account-confirm-btn"
                onClick={async () => {
                  setDeletingAccount(true);
                  try {
                    await api.profile.deleteAccount();
                    toast.success('Konto gelöscht');
                    try { logout?.(); } catch { /* ignore */ }
                    window.location.href = '/';
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
                    setDeletingAccount(false);
                  }
                }}
              >
                {deletingAccount ? 'Lösche…' : 'Endgültig löschen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
